import React, { useContext } from 'react';
import { Button, Alert } from 'react-bootstrap';
import { RouteComponentProps } from 'react-router-dom';
import { HasRoles, RoleUtils } from '@galaxy/lorule-react-commons';
import { useMsal } from '@azure/msal-react';
import { ActionMemo, Approver } from '../../api/actionMemoApiTypes';
import AmCreateModal from '../common/modal/AmCreateModal';
import { AmCreateFormType } from '../common/modal/amCreateformTypes';
import AmProcessModal, { AmProcessFormType } from './modal/AmProcessModal';
import { AmStatusType, ApproverRoutingStatusType } from '../../utils/Constants';
import AmWorkflowModal, { AmWorkflowFormType } from './modal/AmWorkflowModal';
import AmUpdateModal from './modal/AmUpdateModal';
import AmSubmitModal from './modal/AmSubmitModal';
import ConfigContext from '../../contexts/ConfigContext';
import { printActionMemo, printDetail } from '../../api/amDetailApi';
import AmContractSignerModal from '../common/modal/AmContractSignerModal';
import { AmContractSignerFormType } from '../common/modal/AmContractSignerForm';
import { formatNameTitle } from '../../utils/displayUtils';
import EmployeeIdContext from '../../contexts/EmployeeIdContext';
import ContractServiceUpdateModal from './modal/ContractServiceUpdateModal';
import ReplaceProjectManagerModal from './modal/ReplaceProjectManagerModal';

export interface AmDetailFooterProps {
    detail: ActionMemo | null;
    contractSigner?: Approver;
    refresh: () => void;
    refreshApprovalCount: Function;
    editRef: (node: any) => void;
    routerProps: RouteComponentProps;
    canEditDetails: boolean;
}

const AmDetailFooter = (props: AmDetailFooterProps) => {
    const { instance } = useMsal();
    const config = useContext(ConfigContext);
    const roles = RoleUtils.getRoles(instance);

    const employeeId = useContext(EmployeeIdContext);
    const { detail, contractSigner, canEditDetails } = props;

    const canSubmit = !!(
        detail &&
        detail.actionRequested &&
        detail.justification &&
        (!detail.template.showDevCode || (detail.nclDepartmentCode && detail.nclDevCode))
    );

    return (
        detail && (
            <footer className="footer flex-container sticky-bottom border-top mt-3 p-0">
                <div className="ml-auto mt-1 mr-3">
                    {canEditDetails && detail.amStatus === AmStatusType.Draft && (
                        <HasRoles roles={['ActionMemo.User']}>
                            <>
                                <AmUpdateModal
                                    title="Edit Action Memo"
                                    toggleButton={
                                        <Button
                                            variant="outline-primary"
                                            className="ml-auto mobile-hide-btn"
                                            ref={props.editRef}
                                        >
                                            <i className="far fa-edit" /> Edit
                                        </Button>
                                    }
                                    id={detail.id}
                                    amNumber={detail.amNumber}
                                    supNumber={detail.supNumber}
                                    confidential={detail.confidential}
                                    subject={detail.subject}
                                    actionRequested={detail.actionRequested}
                                    justification={detail.justification}
                                    budgetAmount={detail.budgetAmount}
                                    internalCostAmount={detail.internalCostAmount}
                                    revenueAmount={detail.revenueAmount}
                                    creationDate={detail.projectCreationDate}
                                    completionDate={detail.completionDate}
                                    nclDepartmentCode={detail.nclDepartmentCode}
                                    nclDepartmentDescription={detail.nclDepartmentDescription}
                                    nclDevCode={detail.nclDevCode}
                                    nclDevCodeDescription={detail.nclDevCodeDescription}
                                    newCompletionDate={detail.newCompletionDate}
                                    template={detail.template}
                                    onSubmit={props.refresh}
                                />
                                {canSubmit && (
                                    <AmSubmitModal
                                        toggleButton={
                                            <Button
                                                variant="outline-danger"
                                                className="ml-1 mobile-hide-btn"
                                            >
                                                <i className="far fa-share" /> Submit
                                            </Button>
                                        }
                                        amId={detail.id}
                                        amNumber={detail.amNumber}
                                        supNumber={detail.supNumber}
                                        onSubmit={props.refresh}
                                    />
                                )}
                            </>
                        </HasRoles>
                    )}
                    {detail.currentApprover &&
                        (detail.amStatus === AmStatusType.Pending ||
                            detail.amStatus === AmStatusType.Hold) && (
                            <>
                                <AmWorkflowModal
                                    title="Approve Action Memo"
                                    toggleButton={
                                        <Button variant="success" className="ml-1">
                                            <i className="far fa-thumbs-up" /> Approve
                                        </Button>
                                    }
                                    formType={AmWorkflowFormType.APPROVE}
                                    onSubmit={props.refreshApprovalCount}
                                    amId={detail.id}
                                    amNumber={detail.amNumber}
                                    supNumber={detail.supNumber}
                                    comments={detail.comments}
                                    content={
                                        <>
                                            {detail.currentApproverId &&
                                                employeeId &&
                                                detail.currentApproverId !== employeeId && (
                                                    <Alert variant="info">
                                                        You are acting on behalf of{' '}
                                                        <b>{detail.currentApproverName}</b>.
                                                    </Alert>
                                                )}
                                            {contractSigner &&
                                                contractSigner.routingStatus ===
                                                    ApproverRoutingStatusType.CURRENT && (
                                                    <Alert variant="warning">
                                                        You are the contract signer for the document
                                                        on this Action Memo.
                                                    </Alert>
                                                )}

                                            <p>
                                                You are approving this action memo. Please enter
                                                your approval notes below.
                                            </p>
                                        </>
                                    }
                                    routerProps={props.routerProps}
                                />
                                <AmWorkflowModal
                                    title="Return Action Memo"
                                    toggleButton={
                                        <Button variant="danger" className="ml-1">
                                            <i className="far fa-thumbs-down" /> Return
                                        </Button>
                                    }
                                    formType={AmWorkflowFormType.RETURN}
                                    onSubmit={props.refreshApprovalCount}
                                    amId={detail.id}
                                    amNumber={detail.amNumber}
                                    supNumber={detail.supNumber}
                                    comments={detail.comments}
                                    content={
                                        <>
                                            {detail.currentApproverId &&
                                                employeeId &&
                                                detail.currentApproverId !== employeeId && (
                                                    <Alert variant="info">
                                                        You are acting on behalf of{' '}
                                                        <b>{detail.currentApproverName}</b>.
                                                    </Alert>
                                                )}
                                            <Alert variant="warning">
                                                Please be aware that this action will completely
                                                restart the approval workflow, which requires all
                                                new approvals from the beginning.
                                            </Alert>

                                            <p>
                                                You are sending this action memo back to the project
                                                manager, <b>{detail.projectManagerName}</b>. Please
                                                enter notes to <b>{detail.projectManagerName}</b> in
                                                the form below.
                                            </p>
                                        </>
                                    }
                                    routerProps={props.routerProps}
                                />
                                {detail.approvers.length > 0 &&
                                    detail.approvers[detail.approvers.length - 1].routingStatus !==
                                        ApproverRoutingStatusType.CURRENT && (
                                        <AmWorkflowModal
                                            title="Skip Action Memo"
                                            toggleButton={
                                                <Button
                                                    variant="outline-secondary"
                                                    className="ml-1"
                                                >
                                                    <i className="fas fa-forward" /> Skip
                                                </Button>
                                            }
                                            formType={AmWorkflowFormType.SKIP}
                                            onSubmit={props.refreshApprovalCount}
                                            amId={detail.id}
                                            amNumber={detail.amNumber}
                                            supNumber={detail.supNumber}
                                            comments={detail.comments}
                                            content={
                                                <>
                                                    {detail.currentApproverId &&
                                                        employeeId &&
                                                        detail.currentApproverId !== employeeId && (
                                                            <Alert variant="info">
                                                                You are acting on behalf of{' '}
                                                                <b>{detail.currentApproverName}</b>.
                                                            </Alert>
                                                        )}
                                                    <p>
                                                        You are skipping your approval on this
                                                        action memo.
                                                    </p>
                                                </>
                                            }
                                            routerProps={props.routerProps}
                                        />
                                    )}
                            </>
                        )}
                    <Button
                        variant="link"
                        className="btn-link mx-1 mobile-hide-btn"
                        data-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="false"
                    >
                        <i className="fas fa-ellipsis-h text-muted" />
                    </Button>
                    <ul className="dropdown-menu dropdown-menu-right">
                        <HasRoles roles={['ActionMemo.User']}>
                            <>
                                {detail.supNumber === 0 &&
                                    detail.amStatus === AmStatusType.Approved && (
                                        <li>
                                            <AmCreateModal
                                                title="Add Supplement"
                                                toggleButton={
                                                    <Button
                                                        variant="link"
                                                        className="dropdown-menu-modal-toggle dropdown-item"
                                                    >
                                                        <i className="far fa-plus button-icon" />{' '}
                                                        Add Supplement
                                                    </Button>
                                                }
                                                formType={AmCreateFormType.SUPPLEMENT}
                                                amId={detail.id}
                                                subject={detail.subject}
                                                projectManagerId={detail.projectManagerId}
                                                projectManagerName={detail.projectManagerName}
                                                onSubmit={() => props.refresh()}
                                                routerProps={props.routerProps}
                                            />
                                        </li>
                                    )}
                                {(detail.amStatus === AmStatusType.Draft ||
                                    detail.amStatus === AmStatusType.Pending ||
                                    detail.amStatus === AmStatusType.Hold ||
                                    detail.amStatus === AmStatusType.Approved) && (
                                    <li>
                                        <AmCreateModal
                                            title="Copy Action Memo"
                                            toggleButton={
                                                <Button
                                                    variant="link"
                                                    className="dropdown-menu-modal-toggle dropdown-item"
                                                >
                                                    <i className="far fa-clone button-icon" /> Copy
                                                    AM
                                                </Button>
                                            }
                                            formType={AmCreateFormType.COPY}
                                            amId={detail.id}
                                            orgId={detail.orgId}
                                            subject={`COPY OF ${detail.subject}`}
                                            projectManagerId={detail.projectManagerId}
                                            projectManagerName={detail.projectManagerName}
                                            onSubmit={() => props.refresh()}
                                            routerProps={props.routerProps}
                                        />
                                    </li>
                                )}
                            </>
                        </HasRoles>
                        {(canEditDetails || RoleUtils.checkRole(roles, ['ActionMemo.Admin'])) &&
                            detail.amStatus === AmStatusType.Draft && (
                                <li>
                                    <AmProcessModal
                                        title="Cancel Action Memo"
                                        toggleButton={
                                            <Button
                                                variant="link"
                                                className="dropdown-menu-modal-toggle dropdown-item"
                                            >
                                                <i className="far fa-trash-alt button-icon" />{' '}
                                                Cancel AM
                                            </Button>
                                        }
                                        formType={AmProcessFormType.CANCEL}
                                        amId={detail.id}
                                        amNumber={detail.amNumber}
                                        supNumber={detail.supNumber}
                                        onSubmit={() => props.refresh()}
                                    />
                                </li>
                            )}
                        {(detail.amStatus === AmStatusType.Pending ||
                            detail.amStatus === AmStatusType.Hold) && (
                            <HasRoles roles={['ActionMemo.Admin']}>
                                <li>
                                    <AmProcessModal
                                        title="Cancel Action Memo"
                                        toggleButton={
                                            <Button
                                                variant="link"
                                                className="dropdown-menu-modal-toggle dropdown-item"
                                            >
                                                <i className="far fa-trash-alt button-icon" />{' '}
                                                Cancel AM
                                            </Button>
                                        }
                                        formType={AmProcessFormType.CANCEL}
                                        amId={detail.id}
                                        amNumber={detail.amNumber}
                                        supNumber={detail.supNumber}
                                        onSubmit={() => props.refresh()}
                                    />
                                </li>
                            </HasRoles>
                        )}

                        {(canEditDetails ||
                            detail.currentUserIsProjectAdministrator ||
                            RoleUtils.checkRole(roles, ['ActionMemo.Admin'])) && (
                            <>
                                {((detail.supNumber <= 0 &&
                                    (detail.amStatus === AmStatusType.Pending ||
                                        detail.amStatus === AmStatusType.Hold ||
                                        detail.amStatus === AmStatusType.Approved)) ||
                                    (detail.supNumber > 0 &&
                                        (detail.amStatus === AmStatusType.Pending ||
                                            detail.amStatus === AmStatusType.Hold))) && (
                                    <li>
                                        <AmProcessModal
                                            title="Change Completion Date"
                                            toggleButton={
                                                <Button
                                                    variant="link"
                                                    className="dropdown-menu-modal-toggle dropdown-item"
                                                >
                                                    <i className="far fa-calendar button-icon" />{' '}
                                                    Change Completion Date
                                                </Button>
                                            }
                                            formType={AmProcessFormType.CHANGE_COMPLETION_DATE}
                                            amId={detail.id}
                                            amNumber={detail.amNumber}
                                            supNumber={detail.supNumber}
                                            creationDate={detail.projectCreationDate}
                                            completionDate={detail.completionDate}
                                            newCompletionDate={detail.newCompletionDate}
                                            templateMaximumCompletionDays={
                                                detail.template.completionDays
                                            }
                                            onSubmit={() => props.refresh()}
                                        />
                                    </li>
                                )}
                                {detail.supNumber <= 0 && (
                                    <>
                                        {(detail.amStatus === AmStatusType.Pending ||
                                            detail.amStatus === AmStatusType.Hold ||
                                            detail.amStatus === AmStatusType.Approved) &&
                                            detail.template.showDevCode && (
                                                <li>
                                                    <ContractServiceUpdateModal
                                                        title="Update NCL Codes"
                                                        toggleButton={
                                                            <Button
                                                                variant="link"
                                                                className="dropdown-menu-modal-toggle dropdown-item"
                                                            >
                                                                <i className="far fa-edit button-icon" />{' '}
                                                                Update NCL Codes
                                                            </Button>
                                                        }
                                                        amId={detail.id}
                                                        amNumber={detail.amNumber}
                                                        supNumber={detail.supNumber}
                                                        nclDepartmentCode={detail.nclDepartmentCode}
                                                        nclDepartmentDescription={
                                                            detail.nclDepartmentDescription
                                                        }
                                                        nclDevCode={detail.nclDevCode}
                                                        nclDevCodeDescription={
                                                            detail.nclDevCodeDescription
                                                        }
                                                        onSubmit={() => props.refresh()}
                                                    />
                                                </li>
                                            )}
                                        {(detail.amStatus === AmStatusType.Draft ||
                                            detail.amStatus === AmStatusType.Pending ||
                                            detail.amStatus === AmStatusType.Hold ||
                                            detail.amStatus === AmStatusType.Approved) && (
                                            <li>
                                                <ReplaceProjectManagerModal
                                                    title="Replace Project Manager"
                                                    toggleButton={
                                                        <Button
                                                            variant="link"
                                                            className="dropdown-menu-modal-toggle dropdown-item"
                                                        >
                                                            <i className="far fa-user-friends button-icon" />{' '}
                                                            Replace Project Manager
                                                        </Button>
                                                    }
                                                    amId={detail.id}
                                                    amNumber={detail.amNumber}
                                                    personId={detail.projectManagerId}
                                                    onSubmit={() => props.refresh()}
                                                />
                                            </li>
                                        )}
                                    </>
                                )}
                                {detail.confidential ? (
                                    <li>
                                        <AmProcessModal
                                            title="Unmark Confidential"
                                            toggleButton={
                                                <Button
                                                    variant="link"
                                                    className="dropdown-menu-modal-toggle dropdown-item"
                                                >
                                                    <i className="far fa-unlock button-icon" />{' '}
                                                    Unmark Confidential
                                                </Button>
                                            }
                                            formType={AmProcessFormType.UNMARK_CONFIDENTIAL}
                                            amId={detail.id}
                                            amNumber={detail.amNumber}
                                            supNumber={detail.supNumber}
                                            onSubmit={() => props.refresh()}
                                        />
                                    </li>
                                ) : (
                                    <li>
                                        <AmProcessModal
                                            title="Mark Confidential"
                                            toggleButton={
                                                <Button
                                                    variant="link"
                                                    className="dropdown-menu-modal-toggle dropdown-item"
                                                >
                                                    <i className="far fa-lock button-icon" /> Mark
                                                    Confidential
                                                </Button>
                                            }
                                            formType={AmProcessFormType.MARK_CONFIDENTIAL}
                                            amId={detail.id}
                                            amNumber={detail.amNumber}
                                            supNumber={detail.supNumber}
                                            onSubmit={() => props.refresh()}
                                        />
                                    </li>
                                )}
                                {detail.supNumber <= 0 &&
                                    detail.amStatus === AmStatusType.Inactive && (
                                        <li>
                                            <AmProcessModal
                                                title="Reactivate Action Memo"
                                                toggleButton={
                                                    <Button
                                                        variant="link"
                                                        className="dropdown-menu-modal-toggle dropdown-item"
                                                    >
                                                        <i className="far fa-play-circle button-icon" />{' '}
                                                        Reactivate
                                                    </Button>
                                                }
                                                formType={AmProcessFormType.REACTIVATE}
                                                amId={detail.id}
                                                amNumber={detail.amNumber}
                                                supNumber={detail.supNumber}
                                                creationDate={detail.projectCreationDate}
                                                completionDate={detail.completionDate}
                                                templateMaximumCompletionDays={
                                                    detail.template.completionDays
                                                }
                                                onSubmit={() => props.refresh()}
                                            />
                                        </li>
                                    )}
                            </>
                        )}

                        <HasRoles roles={['ActionMemo.Admin']}>
                            <>
                                {detail.amStatus === AmStatusType.Pending && (
                                    <li>
                                        <AmWorkflowModal
                                            title="Hold Action Memo"
                                            toggleButton={
                                                <Button
                                                    variant="link"
                                                    className="dropdown-menu-modal-toggle dropdown-item"
                                                >
                                                    <i className="far fa-hand-paper button-icon" />{' '}
                                                    Hold
                                                </Button>
                                            }
                                            formType={AmWorkflowFormType.HOLD}
                                            amId={detail.id}
                                            amNumber={detail.amNumber}
                                            supNumber={detail.supNumber}
                                            comments={detail.comments}
                                            onSubmit={() => props.refresh()}
                                        />
                                    </li>
                                )}
                                {detail.amStatus === AmStatusType.Hold && (
                                    <li>
                                        <AmWorkflowModal
                                            title="Release Action Memo"
                                            toggleButton={
                                                <Button
                                                    variant="link"
                                                    className="dropdown-menu-modal-toggle dropdown-item"
                                                >
                                                    <i className="far fa-play button-icon" />{' '}
                                                    Release
                                                </Button>
                                            }
                                            formType={AmWorkflowFormType.RELEASE}
                                            amId={detail.id}
                                            amNumber={detail.amNumber}
                                            supNumber={detail.supNumber}
                                            comments={detail.comments}
                                            onSubmit={() => props.refresh()}
                                        />
                                    </li>
                                )}
                                {detail.supNumber === 0 && (
                                    <>
                                        {detail.amStatus === AmStatusType.Approved && (
                                            <li>
                                                <AmProcessModal
                                                    title="Deactivate Action Memo"
                                                    toggleButton={
                                                        <Button
                                                            variant="link"
                                                            className="dropdown-menu-modal-toggle dropdown-item"
                                                        >
                                                            <i className="far fa-stop-circle button-icon" />{' '}
                                                            Deactivate
                                                        </Button>
                                                    }
                                                    formType={AmProcessFormType.DEACTIVATE}
                                                    amId={detail.id}
                                                    amNumber={detail.amNumber}
                                                    supNumber={detail.supNumber}
                                                    onSubmit={() => props.refresh()}
                                                />
                                            </li>
                                        )}
                                        {(detail.amStatus === AmStatusType.Approved ||
                                            (detail.amStatus === AmStatusType.Inactive &&
                                                (detail.priorStatus === AmStatusType.Draft ||
                                                    detail.priorStatus === AmStatusType.Pending ||
                                                    detail.priorStatus === AmStatusType.Hold ||
                                                    detail.priorStatus ===
                                                        AmStatusType.Approved))) && (
                                            <li>
                                                <AmProcessModal
                                                    title="Close Action Memo"
                                                    toggleButton={
                                                        <Button
                                                            variant="link"
                                                            className="dropdown-menu-modal-toggle dropdown-item"
                                                        >
                                                            <i className="far fa-hand-paper button-icon" />{' '}
                                                            Close AM
                                                        </Button>
                                                    }
                                                    formType={AmProcessFormType.CLOSE}
                                                    amId={detail.id}
                                                    amNumber={detail.amNumber}
                                                    supNumber={detail.supNumber}
                                                    onSubmit={() => props.refresh()}
                                                />
                                            </li>
                                        )}
                                        {detail.amStatus === AmStatusType.Closed && (
                                            <li>
                                                <AmProcessModal
                                                    title="Reopen Action Memo"
                                                    toggleButton={
                                                        <Button
                                                            variant="link"
                                                            className="dropdown-menu-modal-toggle dropdown-item"
                                                        >
                                                            <i className="far fa-play-circle button-icon" />{' '}
                                                            Reopen
                                                        </Button>
                                                    }
                                                    formType={AmProcessFormType.REOPEN}
                                                    amId={detail.id}
                                                    amNumber={detail.amNumber}
                                                    supNumber={detail.supNumber}
                                                    creationDate={detail.projectCreationDate}
                                                    completionDate={detail.completionDate}
                                                    templateMaximumCompletionDays={
                                                        detail.template.completionDays
                                                    }
                                                    onSubmit={() => props.refresh()}
                                                />
                                            </li>
                                        )}
                                    </>
                                )}
                                {(detail.amStatus === AmStatusType.Draft ||
                                    detail.amStatus === AmStatusType.Pending ||
                                    detail.amStatus === AmStatusType.Hold) && (
                                    <li>
                                        <AmContractSignerModal
                                            title="Replace Contract Signer"
                                            toggleButton={
                                                <Button
                                                    variant="link"
                                                    className="dropdown-menu-modal-toggle dropdown-item"
                                                >
                                                    <i className="far fa-file-signature button-icon" />{' '}
                                                    {contractSigner ? 'Replace' : 'Add'} Contract
                                                    Signer
                                                </Button>
                                            }
                                            formType={
                                                contractSigner
                                                    ? AmContractSignerFormType.REPLACE
                                                    : AmContractSignerFormType.ADD
                                            }
                                            amId={detail.id}
                                            contractSignerName={
                                                contractSigner
                                                    ? formatNameTitle(
                                                          contractSigner.approverName,
                                                          contractSigner.approverTitle,
                                                      )
                                                    : null
                                            }
                                            onSubmit={() => props.refresh()}
                                        />
                                    </li>
                                )}
                                {detail.statusChangeRequest && (
                                    <li>
                                        <AmProcessModal
                                            title="Cancel Status Change Request"
                                            toggleButton={
                                                <Button
                                                    variant="link"
                                                    className="dropdown-menu-modal-toggle dropdown-item"
                                                >
                                                    <i className="far fa-ban button-icon" /> Cancel
                                                    Status Change Request
                                                </Button>
                                            }
                                            formType={AmProcessFormType.CANCEL_REQUEST}
                                            amId={detail.id}
                                            amNumber={detail.amNumber}
                                            supNumber={detail.supNumber}
                                            onSubmit={() => props.refresh()}
                                        />
                                    </li>
                                )}
                            </>
                        </HasRoles>
                        <li>
                            <Button
                                variant="link"
                                className="dropdown-menu-modal-toggle dropdown-item"
                                onClick={() => {
                                    printActionMemo(
                                        config,
                                        detail.id,
                                        !RoleUtils.checkRole(roles, ['ActionMemo.Admin']),
                                    );
                                }}
                            >
                                <i className="far fa-print button-icon" /> Print AM
                            </Button>
                        </li>
                        {detail.details.map((amDetail) => (
                            <li>
                                <Button
                                    variant="link"
                                    className="dropdown-menu-modal-toggle dropdown-item"
                                    onClick={() => {
                                        printDetail(
                                            config,
                                            detail.id,
                                            amDetail.detailId,
                                            !RoleUtils.checkRole(roles, ['ActionMemo.Admin']),
                                        );
                                    }}
                                >
                                    <i className="far fa-print button-icon" /> Print {amDetail.name}
                                </Button>
                            </li>
                        ))}
                    </ul>
                </div>
            </footer>
        )
    );
};

export default AmDetailFooter;
